import React, { useState } from 'react';
import { IoPlay } from 'react-icons/io5';
import { LuSparkles, LuChevronRight, LuPlus } from 'react-icons/lu';
import { useNodeTheme } from '../Components/NodeTheme';
import { AGENT_COLOR, AGENT_TOOL_MIME, TOOL_TYPES } from './agentCatalog';

const ToolChip = ({ type, size = 26 }) => {
  const meta = TOOL_TYPES[type] || TOOL_TYPES.node;
  const Icon = meta.icon;
  return (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        backgroundColor: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon style={{ fontSize: size * 0.48, color: '#FFFFFF' }} />
    </div>
  );
};

const Dot = ({ color }) => <span style={{ width: '6px', height: '6px', minWidth: '6px', borderRadius: '50%', backgroundColor: color }} />;

const AgentNode = ({ data }) => {
  const theme = useNodeTheme();
  const [dragOver, setDragOver] = useState(false);
  const config = data.config || {};
  const tools = config.tools || [];
  const expanded = data.active || data.forceExpand || dragOver;
  const used = data.usedTools || [];
  const live = data.liveTools || [];
  const title = config.companyName ? `Asistente de ${config.companyName}` : 'Asistente';
  const raised = theme.cardBg === '#FFFFFF' ? '#F3F4F6' : '#232B36';

  const acceptsTool = (event) => Array.from(event.dataTransfer?.types || []).includes(AGENT_TOOL_MIME);

  return (
    <div
      style={{ position: 'relative' }}
      onDragOver={(event) => {
        if (!acceptsTool(event)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(false);
      }}
      onDrop={(event) => {
        if (!acceptsTool(event)) return;
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
        data.onDropTool?.(event.dataTransfer.getData(AGENT_TOOL_MIME));
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-11px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 10px',
          borderRadius: '6px',
          backgroundColor: AGENT_COLOR,
          color: '#FFFFFF',
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '0.7px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          zIndex: 1001,
          pointerEvents: 'none',
        }}
      >
        <IoPlay style={{ fontSize: '9px' }} />
        Inicio
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: expanded ? '320px' : '260px',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: theme.cardBg,
          border: `1px solid ${data.active || dragOver ? AGENT_COLOR : theme.border}`,
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.22)',
          transition: 'width 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '14px' }}>
          <div
            style={{
              width: '34px',
              height: '34px',
              minWidth: '34px',
              borderRadius: '9px',
              backgroundColor: AGENT_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LuSparkles style={{ fontSize: '17px', color: '#FFFFFF' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.7px', textTransform: 'uppercase', color: theme.textSecondary }}>
              Agente IA{data.dirty ? ' · sin guardar' : ''}
            </span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title}
            </span>
            <span style={{ fontSize: '11px', color: theme.textSecondary }}>
              {config.model || 'gpt-4.1-mini'} · memoria {Math.round((config.memoryTtlMin || 120) / 60 * 10) / 10} h
            </span>
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: theme.border }} />

        {!expanded && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px 12px 14px' }}>
            {tools.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {tools.slice(0, 5).map((tool, index) => (
                  <div key={tool.name} style={{ position: 'relative', marginLeft: index ? '-7px' : 0, borderRadius: '50%', boxShadow: `0 0 0 2px ${theme.cardBg}`, zIndex: 5 - index }}>
                    <ToolChip type={tool.type} size={24} />
                  </div>
                ))}
                {tools.length > 5 && (
                  <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: theme.textSecondary }}>+{tools.length - 5}</span>
                )}
              </div>
            )}
            <span style={{ fontSize: '11px', color: theme.textSecondary, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
              {tools.length ? `${tools.length} herramienta${tools.length === 1 ? '' : 's'}` : 'Sin herramientas'}
            </span>
          </div>
        )}

        {expanded && (
          <div style={{ display: 'flex', flexDirection: 'column', padding: '6px 8px' }}>
            <div style={{ padding: '6px 6px 4px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', color: theme.textSecondary }}>
              HERRAMIENTAS · {tools.length}
            </div>
            {tools.map((tool, index) => {
              const selected = data.selectedTool === index;
              const wasUsed = used.includes(tool.name);
              const inUse = live.includes(tool.name);
              return (
                <div
                  key={`${tool.name}-${index}`}
                  className="nodrag"
                  onClick={(event) => {
                    event.stopPropagation();
                    data.onSelectTool?.(index);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 6px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selected || inUse ? raised : 'transparent',
                    border: `1px solid ${selected || inUse ? AGENT_COLOR : 'transparent'}`,
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                  }}
                >
                  <ToolChip type={tool.type} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: theme.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tool.label || tool.name}
                    </span>
                    <span style={{ fontSize: '10px', color: theme.textSecondary, fontStyle: tool.whenToUse ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tool.whenToUse || 'Define cuándo usarla'}
                    </span>
                  </div>
                  {inUse ? (
                    <span style={{ padding: '2px 7px', borderRadius: '5px', backgroundColor: AGENT_COLOR, color: '#FFFFFF', fontSize: '9px', fontWeight: 700 }}>En uso</span>
                  ) : wasUsed ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: theme.textSecondary }}>
                      <Dot color="#2F9E6E" />
                      Usada
                    </span>
                  ) : tool.suggested ? (
                    <span style={{ padding: '1px 6px', borderRadius: '5px', border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: '9px', fontWeight: 600 }}>Sugerida</span>
                  ) : (
                    <LuChevronRight style={{ fontSize: '13px', color: theme.textSecondary }} />
                  )}
                </div>
              );
            })}
            <div style={{ padding: '6px 4px 6px' }}>
              <div
                className="nodrag"
                onClick={(event) => {
                  event.stopPropagation();
                  data.onAddTool?.();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '9px 0',
                  borderRadius: '8px',
                  border: `1px dashed ${dragOver ? AGENT_COLOR : theme.border}`,
                  backgroundColor: dragOver ? raised : 'transparent',
                  color: theme.textPrimary,
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <LuPlus style={{ fontSize: '13px' }} />
                {dragOver ? 'Suéltala aquí' : 'Agregar herramienta'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(AgentNode);
